import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {FlashBanner} from 'shells/flash-banner/flash-banner';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {EmptyState} from './empty-state';
import {getFeatureGate} from './helpers';
import {OnboardingModal} from './onboarding/onboarding-modal';
import {MeetingsTableWrapper} from './tables/meetings-table-wrapper';

type Props = ContextRouter & {
    isConnected: boolean;
    isEnabled: boolean;
    userEmail: string;
    isLoadingIntegration: boolean;
    refetch: () => Promise<any>;
    connectIntegration: (onStart?: () => void, onSuccess?: () => void) => void;
    errorMessage: string | null | undefined;
    setError: (error?: string | null | undefined) => void;
    serviceType: ExternalServiceType;
    user: User | null | undefined;
    startPolling?: (pollInterval: number) => void;
    stopPolling?: () => void;
    enableIntegration: () => void;
    disableIntegration: () => void;
};

export function PageContent(props: Props) {
    const {startPolling, stopPolling} = props;
    const bypassLoadingState = Boolean(
        props.history.location.state && props.history.location.state.openOnboardingModal
    );
    const [isOnboardingModalOpen, setIsOnboardingModalOpen] =
        React.useState<boolean>(bypassLoadingState);

    React.useEffect(() => {
        if (isOnboardingModalOpen && startPolling) {
            startPolling(5000);
        } else if (!isOnboardingModalOpen && stopPolling) {
            stopPolling();
        }

        return () => {
            if (stopPolling) {
                stopPolling();
            }
        };
    }, [startPolling, stopPolling, isOnboardingModalOpen]);

    const featureGate = getFeatureGate(props.serviceType);
    if (featureGate) {
        return featureGate;
    }

    if (!props.user) {
        return null;
    }

    let content = null;
    if (props.isConnected) {
        content = <MeetingsTableWrapper {...props} user={props.user} />;
    } else {
        content = (
            <EmptyState
                setIsOnboardingModalOpen={setIsOnboardingModalOpen}
                serviceType={props.serviceType}
                isLoading={props.isLoadingIntegration}
            />
        );
    }

    return (
        <>
            {props.errorMessage ? (
                <FlashBanner type='error'>{props.errorMessage}</FlashBanner>
            ) : null}
            {content}
            {isOnboardingModalOpen && (
                <OnboardingModal
                    isConnected={props.isConnected}
                    isEnabled={props.isEnabled}
                    userEmail={props.userEmail}
                    isLoading={props.isLoadingIntegration}
                    refetchIntegration={props.refetch}
                    onClose={() => setIsOnboardingModalOpen(false)}
                    connectIntegration={props.connectIntegration}
                    setError={props.setError}
                    errorMessage={props.errorMessage}
                    serviceType={props.serviceType}
                    user={props.user}
                    enableIntegration={props.enableIntegration}
                    disableIntegration={props.disableIntegration}
                    bypassLoadingState={bypassLoadingState}
                />
            )}
        </>
    );
}
